/**
 * Temporary sales block.
 * ---------------------------------------------------------------------------
 *
 * Runs only when cf-sales-block.liquid decided the visitor's country is on the
 * list — otherwise the JSON blob is absent and this exits immediately.
 *
 * Two layers, because the theme adds to cart in two different ways:
 *
 *   1. The buttons. Twenty files render an add-to-cart control, so these are
 *      matched by selector rather than edited one by one. Note the three
 *      exclusions: .button--sm is also worn by the plaque modal trigger, the
 *      plaque auto-fill button and the "request views" button, none of which
 *      add anything to a cart and all of which must keep working.
 *
 *   2. The network. Every configurator posts to /cart/add from its own JS, so
 *      fetch and XMLHttpRequest are wrapped as a backstop. This is what
 *      actually stops the add — the greyed-out button is the explanation.
 *
 * A MutationObserver re-applies the treatment because quick-add buttons arrive
 * with drawer and section re-renders long after load.
 */
(() => {
  const node = document.querySelector('script[type="application/json"][data-cf-sales-block]');
  if (!node) return;

  let config;

  try {
    config = JSON.parse(node.textContent);
  } catch (e) {
    return; // A malformed blob must not take add-to-cart down for everyone
  }

  if (!config || !config.notice) return;

  /**
   * Only classes that mark a genuine add-to-cart control.
   *
   * `.button--sm` looks like the obvious candidate and is NOT usable: it is a
   * generic button style, worn by mega-menu links like "Shop All" and "Learn
   * More". Selecting on it greys out half the navigation and still misses the
   * real button.
   */

  /* Full-size buttons: greyed out, with the explanation underneath. */
  const ATC_PRIMARY = [
    '.add-all-to-cart',            // cc / frame / blok / trio configurators
    '.add-to-cart_grand',          // grand configurator
    '#collections-atc-btn',        // bundle configurator (full-width, inside .atc_group)
    'buy-buttons [type="submit"]', // standard product form (buy-buttons.liquid, sticky bar)
    'product-form [type="submit"]',
    'form[action*="/cart/add"] [type="submit"]'
  ].join(',');

  /**
   * Compact quick-add on the product cards: removed outright, no notice. The
   * card is a couple of hundred pixels wide and the explanation runs to two
   * sentences — it would dwarf the card it sits under. The visitor gets the
   * reason on the product page, where there is room for it.
   *
   * Scoped to `button` on purpose: the out-of-stock state renders a <div> with
   * the same attributes carrying the "Sold out" label, and that should stay.
   *
   * ON THE ATTRIBUTE, NOT A CLASS (2026-09-30). This was `button.button--global-atc`
   * until that class was taken off the cards, it being a styling class that also
   * happened to be the only usable hook. `data-atc-source` is on every one of these
   * buttons and nothing else in the theme — six of them, the card quick-adds and the
   * city overview's — so it selects exactly what the class did and cannot be removed
   * by a change of appearance. The full-size buttons above are a separate list and
   * carry no such attribute, so they are still greyed with a notice rather than hidden.
   */
  const ATC_COMPACT = 'button[data-atc-source]';

  const style = document.createElement('style');
  style.textContent =
    '[data-cf-sales-blocked]{opacity:.4 !important;pointer-events:none !important;cursor:not-allowed !important;}' +
    '[data-cf-sales-hidden]{display:none !important;}' +
    // grid-column/width so the notice takes its own full-width row rather than
    // squeezing in beside the button when the wrapper is a grid or flex row.
    '.cf-sales-block-notice{margin-top:.5rem;font-size:.8125rem;line-height:1.35;width:100%;grid-column:1/-1;flex-basis:100%;}';
  document.head.appendChild(style);

  /**
   * Directly after the button, nothing clever. The obvious move is to anchor to
   * a wrapper like .atc_group so the notice clears the payment icons — but on
   * the CUBE page .atc_group is 636px tall against a 45px button, and the
   * notice ended up half a screen adrift from the thing it explains.
   */
  function noticeAnchor(button) {
    return button;
  }

  function blockButton(button) {
    // Re-asserted on every pass, not just the first: the configurators own
    // their button state (cc_configurator's lockATCButtons / updateAddToCartState
    // both write `disabled`) and will happily set it back to false after us.
    button.setAttribute('aria-disabled', 'true');
    if ('disabled' in button) button.disabled = true;

    if (button.dataset.cfSalesBlocked) return;
    button.dataset.cfSalesBlocked = 'true';

    const anchor = noticeAnchor(button);
    const host = anchor.parentElement || anchor;
    if (host.querySelector(':scope > .cf-sales-block-notice')) return;

    const notice = document.createElement('p');
    notice.className = 'cf-sales-block-notice text-subdued';
    notice.innerHTML = config.notice;
    anchor.after(notice);
  }

  function hideButton(button) {
    if (button.dataset.cfSalesHidden) return;
    button.dataset.cfSalesHidden = 'true';
    button.setAttribute('aria-hidden', 'true');
    if ('disabled' in button) button.disabled = true;
  }

  function apply() {
    document.querySelectorAll(ATC_PRIMARY).forEach(blockButton);
    document.querySelectorAll(ATC_COMPACT).forEach(hideButton);
  }

  // Layer 2: the actual stop.
  /**
   * The endpoint is /cart/add.js (or .json, or bare /cart/add for a plain form
   * post), and it carries a locale prefix on the translated storefronts —
   * /de/cart/add.js. Matching only a bare "/cart/add" suffix silently lets the
   * real calls through.
   */
  const CART_ADD = /\/cart\/add(\.js|\.json)?\/?$/;

  const isCartAdd = (url) => {
    try {
      return CART_ADD.test(new URL(url, window.location.origin).pathname);
    } catch (e) {
      return false;
    }
  };

  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (isCartAdd(url)) {
      return Promise.reject(new Error('Add to cart is unavailable in ' + (config.country || 'this country') + '.'));
    }
    return nativeFetch.apply(this, arguments);
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cfBlocked = isCartAdd(url);
    return nativeOpen.apply(this, arguments);
  };

  const nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__cfBlocked) {
      this.dispatchEvent(new Event('error'));
      return;
    }
    return nativeSend.apply(this, arguments);
  };

  // Non-JS product forms post normally; stop those too.
  document.addEventListener(
    'submit',
    (event) => {
      const action = event.target && event.target.getAttribute('action');
      if (action && isCartAdd(action)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  function start() {
    apply();
    new MutationObserver(apply).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
